import React, { useCallback } from "react";
import _, { isUndefined } from 'lodash';
import TableContainer from 'components/TableContainer';
import { CellRenderers, getFilterProps, Sorters } from 'utils/tableUtils';
import message from "components/CustomMessage";
import ActionMenuV2 from 'components/ActionMenuV2';
import { PermissionSetMap } from 'constants/RBACConstants';
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import ModalUtil from "utils/modalUtil";
import GitRepoConfigForm from "./GitRepoConfigForm";
import { isAnyAdmin } from 'utils/authService';
import RepositoryExpand from './RepositoryExpand';
import { AdditionalSettingIcon, UnlinkIcon, RefreshIcon, EditIcon} from 'components/Icons';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useActiveUserContext } from 'contexts/ActiveUserContext';
import { Tooltip, Typography, Divider, Button } from 'antd';
import User from 'components/User';
import AuthorisedContainer from 'components/AuthorisedContainer';
import { useFeatureFlag } from "contexts/FeatureFlagContext";


const RepoListingTable = ({data, onUpdateConfigComponent, noExtras, ...props}) => {
    const history = useHistory();
    const { accessOnAllProduct, isArchiveBU } = useActiveUserContext()
    const toolName = _.toUpper(props?.id);
    const featureFlags = useFeatureFlag();

    const items = [{
        type: "delete",
        text: "Remove Installation",
        icon: <UnlinkIcon/>,
        permission: PermissionSetMap.DELETE_TOOL
    },
    ...(
        isAnyAdmin && (toolName === "GITLAB" || toolName === "AZURE_REPOS" || (featureFlags.SIMPLIFIED_ONBOARDING && toolName === "BITBUCKET")) ? [
            {
                type: "edit",
                text: "Edit Installation",
                icon: <EditIcon/>,
                permission: PermissionSetMap.EDIT_TOOL_CONFIG
            }
          ]
        : []),
   ]

    const editConfig = useCallback((data) => {
        ModalUtil.show({
            content: (
                <GitRepoConfigForm
                    displayName={data.displayName}
                    id={props.name}
                    configData={data} 
                    onUpdateConfigComponent={onUpdateConfigComponent}
                    afterClose={props.afterClose}
                />
            ),
        });
    }, [
        props.name,
        onUpdateConfigComponent,
        props.afterClose
    ]);

    const deleteConfig = async (id) => {
        try{
            await ToolsIntegrationsAPI.deleteGitInstallations(id).promise;
            message.success("Deleted successfully");
            onUpdateConfigComponent();
        } catch (err) {
            console.error(err);
            throw err;
        }
    }

    const onClick = async (_, record) => {
        switch(record.name){
          case "edit":{
            const data = { ...record.data };
              editConfig(data);
            break;  
          }
          case "delete":
              await deleteConfig(record.data?.id)
            break;
          default:
            break;
        }
    }

    const syncRepos = async(toolName) => {
        try {
            const messageReference = message.loading('Updating...')
            await ToolsIntegrationsAPI.syncRepos(toolName).promise;
            messageReference();
            message.success("Syncing repos, it may take some time.");
        } catch (e){
            console.log(e);
        }
    }

    const columns = [
        {
            title:"ID",
            dataIndex: "id",
            sorter: Sorters.NUMBER("id"),
            render: (id) => _.toString(id),
            ...getFilterProps({
                dataIndex: "id",
                dataSource: data,
                onlySearch: true,
                isMultipleValueSupported: true
            }),
            width: 75
        }, {
            title:"Name",
            dataIndex: "name",
            width: 120,
            ...(toolName === "BITBUCKET" ? {
                ...getFilterProps({
                    dataIndex: "name",
                    dataSource: data
                }),
                sorter: Sorters.STRING("name")
            } : {}),
        },...(toolName === "GITLAB" || toolName === "BITBUCKET" ? [{
            title: "Username",
            dataIndex: "account",
            render: (value, record) => CellRenderers.VALUE_OR_NA(value || record.username),
            width: 150,
            ellipsis: true
        }] : []),{
            title: "Account Type",
            dataIndex: "accountType",
            render: CellRenderers.VALUE_OR_NA,
            width: 100
        }, 
        {
            title: "Created By",
            dataIndex: "createdBy",
            sorter: Sorters.STRING("createdBy"),
            render: CellRenderers.VALUE_OR_NA,
            width: 200,
            ellipsis: true
        }, {
            title: "Updated By",
            dataIndex: "updatedBy",
            sorter: Sorters.STRING("updatedBy"),
            render: (text) => <User userEmail={text} />,
            width: 230,
            ellipsis: true
        }, {
            title: "Created On",
            dataIndex: "createdAt",
            sorter: Sorters.DATE("createdAt"),
            render: CellRenderers.TIME_RELATIVE,
            width: 150
        }, {
            title: "Updated On",
            dataIndex: "updatedAt",
            sorter: Sorters.DATE("updatedAt"),
            render: CellRenderers.TIME_RELATIVE,
            width: 150
        },
        {
            title: "Action",
            key: 'action',
            width: 50,
            render: (record) => {
        
                return (
                    <ActionMenuV2 items={items} onClick={onClick} data={record} />
                )
            }
        }
    ]

    const renderSetupLink = () => {
        switch(toolName) {
            case "GITLAB":
                return <Typography.Link href={"/api/jwt/zendesk/sso?return_to=https://support.armorcode.com/hc/en-us/articles/19601269475347-GitLab"} target="_blank" className='m-r-sm'>How to setup GitLab ?</Typography.Link>;
            case "AZURE_REPOS":
                return <Typography.Link href={"/api/jwt/zendesk/sso?return_to=https://support.armorcode.com/hc/en-us/articles/19601376468115-Azure-Repos"} target="_blank" className='m-r-sm'>How to setup Azure Repos ?</Typography.Link>;
            case "BITBUCKET": 
                return <Typography.Link href={'/api/jwt/zendesk/sso?return_to=https://support.armorcode.com/hc/en-us/articles/19633646963475-Bitbucket'} target="_blank">How to setup Bitbucket ?</Typography.Link>
            default:
                return null;
        }
    };

    const extras = () => {
        const showAdditionSettings = ["GITLAB"].includes(toolName);
        return (
            !noExtras && <>
                <>
                    <AuthorisedContainer permission={PermissionSetMap.WRITE_TOOLS} tooltip={isArchiveBU ? "Sync is not supported for Archive BU" :_.isEmpty(data) ? '': "Sync repos"}>
                        <Button type='text' size='small' disabled={isArchiveBU ? true :_.isEmpty(data)} onClick={()=>syncRepos(toolName)}><RefreshIcon /></Button>
                    </AuthorisedContainer>
                    <Divider type="vertical"/>
                </>
                
                {renderSetupLink()}
                {accessOnAllProduct && showAdditionSettings && <Tooltip title="Additional Settings" placement="topRight">
                    <AdditionalSettingIcon onClick={() => history.push(`/integrations/${_.toLower(toolName)}/listing`)} className="clickable"/>
                </Tooltip>}
            </>
        )
    }

    return (
       <TableContainer
            enableScroll={true}
            loading={isUndefined(data)}
            columns={columns}
            dataSource={data}
            title="Configurations"
            defaultSort={{column: "createdAt", order: "descend"}}
            rowKey={"id"}
            hideExpandAll={true}
            expandable={{
                expandedRowRender: original => <RepositoryExpand installation={original} toolName={props?.id} />
            }}
            extras={extras()}
       />
    )
}

export default RepoListingTable;